// src/components/chat/SubscriberSearchPicker.jsx
//
// Type-ahead subscriber picker for Navigators/Admins — mirrors
// PatientSearchPicker.jsx exactly (same shape, same reasoning: reuses an
// existing search endpoint rather than a flat list, since the subscriber
// set could grow into the hundreds the same way a navigator's patient set
// does). Backs two call sites:
//   - SubscriberLinkPanel.jsx (Navigator linking a patient to a subscriber)
//   - TeamInboxPage.jsx (Navigator starting a new Subscriber↔Navigator chat)
//
// requireActivePlan (TeamInboxPage only): a subscriber can only be messaged
// once their plan is ACTIVE (ConversationEligibilityService.
// canMessageSubscriberNavigator). The search result's subscriptionStatus
// lets the picker show that up front — such rows are disabled with a short
// note instead of failing with an error toast after the click. When the
// backend doesn't send subscriptionStatus yet, nothing is disabled and the
// server stays the judge. Linking a patient has no such rule, so
// SubscriberLinkPanel leaves this off.

import React, { useState, useEffect, useRef } from 'react';
import { subscriberSearchAPI } from '../../services/api';

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

const PLAN_NOTES = {
  PENDING: 'Plan not active yet',
  EXPIRED: 'Plan expired',
  CANCELLED: 'Plan cancelled',
};

function inactivePlanNote(subscriber) {
  const status = subscriber.subscriptionStatus;
  if (!status || status === 'ACTIVE') return null;
  return `${PLAN_NOTES[status] || 'Plan not active'} — can’t message yet`;
}

export default function SubscriberSearchPicker({ onSelect, onClose, requireActivePlan = false }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      setError(null);
      subscriberSearchAPI.search(query.trim())
        .then((res) => {
          // Defensive, same as PatientSearchPicker.jsx — never trust a
          // response shape enough to skip this check.
          setResults(Array.isArray(res.data) ? res.data : []);
        })
        .catch((e) => setError(e.response?.data?.message || e.message || 'Search failed.'))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  async function handlePick(subscriber) {
    setStarting(subscriber.id);
    try {
      await onSelect({ id: subscriber.id, name: `${subscriber.firstName} ${subscriber.lastName}` });
    } finally {
      setStarting(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Find a subscriber"
      className="dialog-backdrop"
      onClick={onClose}
    >
      <div className="dialog dialog--picker" onClick={(e) => e.stopPropagation()}>
        <div className="picker-head">
          <div className="picker-title">Find a subscriber</div>
          <button type="button" className="picker-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="picker-search">
          <input
            ref={inputRef}
            type="text"
            className="form-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email…"
            aria-label="Search subscribers"
          />
        </div>

        <div className="picker-results">
          {!query.trim() && (
            <div className="picker-msg">Start typing a subscriber's name or email.</div>
          )}
          {loading && <div className="picker-msg">Searching…</div>}
          {error && <div className="picker-msg picker-msg--error">{error}</div>}
          {!loading && !error && query.trim() && results.length === 0 && (
            <div className="picker-msg">No subscribers found for "{query.trim()}".</div>
          )}

          {results.map((s) => {
            const planNote = requireActivePlan ? inactivePlanNote(s) : null;
            return (
              <button
                key={s.id}
                type="button"
                className="picker-row"
                onClick={() => handlePick(s)}
                disabled={!!planNote || starting === s.id}
                style={planNote ? { opacity: 0.6 } : undefined}
              >
                <div className="avatar avatar--sm">
                  {initials(`${s.firstName} ${s.lastName}`)}
                </div>
                <div className="picker-row__body">
                  <div className="picker-row__name">
                    {s.firstName} {s.lastName}
                  </div>
                  {s.email && <div className="picker-muted">{s.email}</div>}
                  {planNote && <div className="picker-muted">{planNote}</div>}
                </div>
                {starting === s.id && <span className="picker-muted">⏳</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
